import { useContext, useEffect } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { usePermission } from '../../context/PermissionContext';
import { resolveRole } from '../../utils/roleUtils';

/**
 * Yetki doğrulama bileşeni
 * 
 * Bu bileşen, çocuk bileşenleri sadece belirli rolleri veya izinleri olan
 * kullanıcılara göstermek için kullanılır.
 * 
 * @param {Object} props - Bileşen props'ları
 * @param {React.ReactNode} props.children - Çocuk bileşenler
 * @param {Array<string>} [props.requiredRoles] - Gerekli roller (bunlardan birine sahip olmalı)
 * @param {Array<string>} [props.anyPermission] - Gerekli izinlerden HERHANGİ BİRİ (birine sahip olmak yeterli - OR mantığı) 
 * @param {Array<string>} [props.requiredPermissions] - Gerekli izinler (tümüne sahip olmalı - AND mantığı)
 * @param {boolean} [props.renderNull=true] - false ise, izni olmayan kullanıcılara fallback render edilir
 * @param {React.ReactNode} [props.fallback=null] - İzin yoksa gösterilecek içerik
 * @returns {React.ReactNode}
 */
const PermissionGuard = ({ 
  children, 
  requiredRoles = [], 
  requiredPermissions = [],
  anyPermission = [],
  permission = [], // Eski prop, geriye uyumluluk için
  renderNull = true,
  fallback = null
}) => {
  const { user } = useContext(AuthContext);
  const { hasPermission, hasRole } = usePermission();

  // Debug: Önemli izinler için özel kontrol
  useEffect(() => {
    // Özellikle create_refund veya view_admin_panel izinleri istendiyse logla
    const checkCreateRefund = anyPermission.includes('create_refund') || 
                             requiredPermissions.includes('create_refund');
    
    const checkViewAdminPanel = anyPermission.includes('view_admin_panel') || 
                               requiredPermissions.includes('view_admin_panel');
    
    if (checkCreateRefund) {
      console.log('PermissionGuard - create_refund kontrol ediliyor:', hasPermission('create_refund'));
      console.log('PermissionGuard - create_refund izin türü:', 
                  anyPermission.includes('create_refund') ? 'anyPermission' : 'requiredPermissions');
    }
    
    if (checkViewAdminPanel) {
      console.log('PermissionGuard - view_admin_panel kontrol ediliyor:', hasPermission('view_admin_panel'));
      console.log('PermissionGuard - view_admin_panel izin türü:', 
                  anyPermission.includes('view_admin_panel') ? 'anyPermission' : 'requiredPermissions');
    }
  }, [anyPermission, requiredPermissions, hasPermission]);

  // Kullanıcı giriş yapmamışsa hiçbir şey gösterme
  if (!user) {
    return renderNull ? null : fallback;
  }

  // Geriye uyumluluk: permission prop'u varsa anyPermission'a ekle
  const anyPermissionsNeeded = [
    ...anyPermission, 
    ...(Array.isArray(permission) ? permission : [permission])
  ].filter(Boolean); // Boş değerleri filtrele
  
  // 1. Rol kontrolü - Öncelikle rol bazlı kontrol yap
  let roleCheckPassed = true;
  
  if (requiredRoles.length > 0) {
    // En az bir role sahip olmalı (OR mantığı)
    roleCheckPassed = requiredRoles.some(role => hasRole(role));
  }
  
  // Rol kontrolü başarısız olduysa, izinlere bakmaya gerek yok
  if (!roleCheckPassed) {
    return renderNull ? null : fallback;
  }
  
  // 2. İzin kontrolleri (rol kontrolü başarılı olduysa):
  
  // 2.a. Her iki izin grubu da boşsa, izin kontrolü atlanır
  const skipPermissionCheck = anyPermissionsNeeded.length === 0 && requiredPermissions.length === 0;
  
  // 2.b. Herhangi bir izin (OR) - anyPermission prop'u
  const hasAnyOfPermissions = anyPermissionsNeeded.length === 0 || 
    anyPermissionsNeeded.some(perm => hasPermission(perm));
    
  // 2.c. Tüm gerekli izinler (AND) - requiredPermissions prop'u
  const hasAllRequiredPermissions = requiredPermissions.length === 0 || 
    requiredPermissions.every(perm => hasPermission(perm));
  
  // 3. İzin kontrolü: Her iki izin grubunu da geçerse başarılı
  const permissionCheckPassed = skipPermissionCheck || 
    (hasAnyOfPermissions && hasAllRequiredPermissions);

  // Hem rol kontrolü hem de izin kontrolü başarılıysa, içeriği göster
  if (roleCheckPassed && permissionCheckPassed) {
    return children;
  }

  // İzin veya rol kontrolü başarısızsa, null veya fallback döndür
  return renderNull ? null : fallback;
};

export default PermissionGuard; 